import { createContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

const ThemeContext = createContext()

const getSavedThemeName = () => {
  if (typeof window === 'undefined') return null

  try {
    const savedTheme = window.localStorage.getItem('themeName')
    return savedTheme === 'dark' || savedTheme === 'light' ? savedTheme : null
  } catch {
    return null
  }
}

const ThemeProvider = ({ children }) => {
  const [themeName, setThemeName] = useState(() => getSavedThemeName() || 'light')

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined
    }

    const savedTheme = getSavedThemeName()
    const systemDarkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    if (!savedTheme) {
      setThemeName(systemDarkMediaQuery.matches ? 'dark' : 'light')
    }

    const onSystemThemeChange = (event) => {
      if (!getSavedThemeName()) {
        setThemeName(event.matches ? 'dark' : 'light')
      }
    }

    if (typeof systemDarkMediaQuery.addEventListener === 'function') {
      systemDarkMediaQuery.addEventListener('change', onSystemThemeChange)
    } else if (typeof systemDarkMediaQuery.addListener === 'function') {
      systemDarkMediaQuery.addListener(onSystemThemeChange)
    }

    return () => {
      if (typeof systemDarkMediaQuery.removeEventListener === 'function') {
        systemDarkMediaQuery.removeEventListener('change', onSystemThemeChange)
      } else if (typeof systemDarkMediaQuery.removeListener === 'function') {
        systemDarkMediaQuery.removeListener(onSystemThemeChange)
      }
    }
  }, [])

  const toggleTheme = () => {
    const name = themeName === 'dark' ? 'light' : 'dark'

    try {
      window.localStorage.setItem('themeName', name)
    } catch {
      // no-op in restricted environments
    }

    setThemeName(name)
  }

  return (
    <ThemeContext.Provider value={[{ themeName, toggleTheme }]}>
      {children}
    </ThemeContext.Provider>
  )
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export { ThemeProvider, ThemeContext }
